import React, { createElement, Fragment, SetStateAction, useEffect, useRef } from 'react';
import { render } from 'react-dom';
import { SearchClient } from 'algoliasearch/lite';
import { useInstantSearch, useSearchBox } from 'react-instantsearch-hooks';
import {
  autocomplete,
  AutocompleteOptions,
  OnStateChangeProps,
  AutocompletePlugin,
} from '@algolia/autocomplete-js';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { ClearRefinementsProps } from 'react-instantsearch-hooks-web';
import { BaseItem } from '@algolia/autocomplete-core';
import { debounce } from 'lib/util/debounce';
import { classnames } from 'tailwindcss-classnames';
import { routerPropsFactory } from './routeMappers';
import { NmdpUiIndexState, RouteQueryParameters, RouteState } from './SearchTypes';
import { RouterProps } from 'instantsearch.js/es/middlewares';
import { env } from 'lib/env';
import { createSearchClient } from 'lib/util/algolia';
import { useFastDeepEqualMemo } from 'lib/react';
import fnNoOp from 'lib/util/no-op';

type ResetAutocompleteCallbackType = () => void;
type AutocompleteProps = Partial<AutocompleteOptions<BaseItem>> &
  ClearRefinementsProps & {
    asSearchBox?: boolean;
    className?: string;
    skipSuggestions?: boolean;
    setResetCallback?: (cb: ResetAutocompleteCallbackType) => void;
  };

type AutocompleteWithSearchProps = AutocompleteProps & {
  isOnSearchPage: boolean;
  setResetCallback?: (cb: ResetAutocompleteCallbackType) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createSuggestionPlugins = (searchClient: SearchClient): AutocompletePlugin<any, any>[] => {
  /// Store the users last 3 searches in local storage
  const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: 'search',
    limit: 3,
  });

  /// And allow for up to 6 suggestions to be pulled from the query index
  const querySuggestionsPlugin = createQuerySuggestionsPlugin({
    searchClient,
    indexName: env.ALGOLIA_QUERY_SUGGESTIONS_INDEX_NAME,
    getSearchParams() {
      // This creates a shared `hitsPerPage` value once the duplicates
      // between recent searches and Query Suggestions are removed.
      if (recentSearchesPlugin.data == null) {
        throw new Error('Prettier can be a pain');
      }
      return recentSearchesPlugin.data.getAlgoliaSearchParams({
        hitsPerPage: 6,
      });
    },
  });
  return [recentSearchesPlugin, querySuggestionsPlugin];
};

const searchClient = createSearchClient();
function AutocompleteWithSearch({
  className,
  asSearchBox,
  skipSuggestions,
  isOnSearchPage,
  setResetCallback,
  ...autocompleteProps
}: AutocompleteWithSearchProps) {
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const { setIndexUiState } = useInstantSearch();
  // Need this so search query parameters are processed
  useSearchBox();
  /**
   * This, on the other hand, does look to actively be set and ready.
   */
  const memorizeProps = useFastDeepEqualMemo(() => autocompleteProps, [autocompleteProps]);

  useEffect(() => {
    if (!autocompleteContainer.current || !searchClient) {
      return fnNoOp;
    }
    const { cancel, debouncedFn: debouncedSetInstantSearchUiState } = debounce(
      (state: SetStateAction<NmdpUiIndexState>) => {
        if (autocompleteContainer.current) {
          setIndexUiState(state);
        }
      },
      500
    );

    // Craete routing object, and if available to us attach to it
    const routing = asSearchBox
      ? routerPropsFactory({ addSortInfoToRoute: true })
      : // Hard-casting undefined as a router.
        (undefined as unknown as RouterProps<RouteState, RouteQueryParameters>);
    const { query: initialQuery } = routing?.router?.read() ?? { query: '' };

    const autocompleteInstance = autocomplete({
      routing,
      container: autocompleteContainer.current,
      initialState: { query: initialQuery },
      onReset() {
        setIndexUiState((prev) => {
          return {
            query: '',
            configure: prev.configure,
            sortBy: prev.sortBy,
          };
        });
      },
      onSubmit({ state }) {
        setIndexUiState((prev) => {
          return {
            ...prev,
            query: state.query,
          };
        });
      },
      onStateChange: asSearchBox
        ? ({ prevState, state }: OnStateChangeProps<BaseItem>) => {
            if (prevState.query !== state.query) {
              debouncedSetInstantSearchUiState((prev) => {
                return {
                  ...prev,
                  query: state.query,
                };
              });
            }
            return state;
          }
        : undefined,
      renderer: {
        createElement,
        Fragment,
        // @ts-ignore We are seeing a type incompatibility between Algolia and React's version of render; this incompatibility does not seem to have runtime impact
        render,
      },
      plugins: skipSuggestions ? [] : createSuggestionPlugins(searchClient),
      ...memorizeProps,
    });
    if (setResetCallback) {
      setResetCallback(() => () => {
        if (autocompleteContainer.current) {
          autocompleteInstance.setQuery('');
        }
      });
    }

    return () => {
      // Cancels any pending updates to the querybox
      cancel();
      if (setResetCallback) {
        setResetCallback(fnNoOp);
      }
      // And then SELF DESTRUCT!!!!!!!
      autocompleteInstance.destroy();
    };
  }, [
    isOnSearchPage,
    asSearchBox,
    setIndexUiState,
    skipSuggestions,
    memorizeProps,
    setResetCallback,
  ]);

  return <div className={`${classnames('aa-Wrapper')} ${className}`} ref={autocompleteContainer} />;
}

export function Autocomplete(props: AutocompleteProps) {
  const { sitecoreContext } = useSitecoreContext();
  if (sitecoreContext.pageEditing === true) {
    const { className } = props;
    return (
      <div className={`${classnames('aa-Wrapper')} ${className}`}>
        <div className="aa-Autocomplete">
          <form className="aa-Form">
            <div className="aa-InputWrapper">
              <input
                className="aa-Input"
                aria-roledescription="Search"
                aria-autocomplete="list"
                aria-label="Search Box"
              />
            </div>
          </form>
        </div>
      </div>
    );
  }
  const isOnSearchPage = sitecoreContext.hasSearch === true;
  return <AutocompleteWithSearch isOnSearchPage={isOnSearchPage} {...props} />;
}

export default Autocomplete;
